import Seo from "../../components/seo";
import Layout from "../../components/layout";
import Header from "../../components/header";
import Footer from "../../components/footer";
import ServiceHero from "../../components/ServiceHero";
import ServiceCTA from "../../components/ServiceCTA";
import { CardColumn, HorizontalScroll } from "../../components/sections";
import { FiZap, FiTarget, FiCode, FiCpu, FiTrendingUp, FiBriefcase, FiStar, FiLock } from 'react-icons/fi';
import { usePageMetadata } from "../../hooks/usePageMetadata";

const AIForStartupsPage = () => {
  usePageMetadata({
    title: 'AI for Startups - Bercastle',
    description: 'Prototype development and generative AI advisory for startups. Quick MVPs to validate demand and showcase AI capabilities.',
    pageType: 'service',
    content: {
      service: {
        name: "AI for Startups",
        tagline: "Fast-Track Your AI Innovation",
        description: "Prototype development and generative AI advisory specifically designed for startups. Quick MVPs to validate demand.",
        url: "/services/ai-for-startups"
      },
      capabilities: [
        {
          name: "Prototype Development",
          description: "Quick MVPs to validate demand and showcase AI capabilities. Get your idea off the ground fast.",
          deliverables: ["Working MVP in weeks", "Core AI functionality", "User-friendly interface", "Demo-ready product", "Technical documentation"]
        },
        {
          name: "Generative AI Advisory",
          description: "We guide companies exploring generative AI by helping them harness models, tools, and workflows.",
          services: ["AI strategy development", "Technology selection", "Model evaluation", "Implementation planning", "Best practices guidance"]
        },
        {
          name: "Production Implementation",
          description: "Scale from prototype to production with robust, scalable AI systems."
        },
        {
          name: "AI Integration",
          description: "Seamlessly integrate AI capabilities into your existing products."
        }
      ],
      technologies: ["OpenAI GPT models", "Anthropic Claude", "Google Gemini", "LangChain", "Vector databases", "Custom ML models"],
      value_propositions: [
        "Speed: Rapid prototyping and deployment",
        "Cost-Effective: Startup-friendly pricing",
        "Expertise: Deep AI and ML knowledge",
        "Flexibility: Adapt to changing requirements",
        "Support: Ongoing technical guidance"
      ],
      meta: {
        source_url: "https://bercastle.com/services/ai-for-startups",
        service_category: "AI for Startups"
      }
    }
  });
  const startupServices = [
    {
      icon: FiTarget,
      title: 'Prototype Development',
      description: 'Quick MVPs to validate demand and showcase AI capabilities. Get your idea off the ground fast with lightweight, functional prototypes.'
    },
    {
      icon: FiZap,
      title: 'Generative AI Advisory',
      description: 'We guide companies exploring generative AI by helping them harness models, tools, and workflows tailored to their goals.'
    },
    {
      icon: FiCode,
      title: 'Custom AI Development',
      description: 'We design intelligent systems that interpret data, uncover insights, forecast outcomes, and streamline complex workflows.'
    },
    {
      icon: FiCpu,
      title: 'Custom LLM & AI Agents',
      description: 'Build fine-tuned models and autonomous assistants tailored to your domain, optimized for your specific use cases.'
    }
  ];

  const aiSolutions = [
    {
      icon: FiTrendingUp,
      title: 'AI-powered MVPs',
      description: 'Quick and scalable prototypes to validate your AI ideas'
    },
    {
      icon: FiCpu,
      title: 'Custom AI agents',
      description: 'Autonomous assistants tailored to your business needs'
    },
    {
      icon: FiZap,
      title: 'Chatbots and virtual assistants',
      description: 'Intelligent conversational AI for customer engagement'
    },
    {
      icon: FiTarget,
      title: 'Intelligent recommender systems',
      description: 'Personalized recommendations that drive conversions'
    },
    {
      icon: FiCode,
      title: 'Document and data intelligence',
      description: 'Extract insights from unstructured data automatically'
    },
    {
      icon: FiZap,
      title: 'Smart search and Q&A',
      description: 'Semantic search and natural language understanding'
    },
    {
      icon: FiStar,
      title: 'Predictive analytics dashboards',
      description: 'Data-driven insights for better decision making'
    },
    {
      icon: FiCode,
      title: 'Voice and vision interfaces',
      description: 'Multimodal AI for richer user interactions'
    }
  ];

  const whyItems = [
    {
      icon: FiTrendingUp,
      title: 'Speed to Market',
      description: 'We turn ideas into prototypes fast: no red tape, no guesswork, just results.'
    },
    {
      icon: FiBriefcase,
      title: 'Cost-Efficient Scalability',
      description: 'Leverage lightweight AI and open-source models to minimize costs.'
    },
    {
      icon: FiStar,
      title: 'Tech with Impact',
      description: 'Align AI solutions with your core business KPIs from day one.'
    },
    {
      icon: FiLock,
      title: 'Security & Compliance',
      description: 'Privacy-first implementation: GDPR, HIPAA, and SOC ready when needed.'
    }
  ];
  return (
    <Layout classmain="ai-for-startups-page">
      <Seo
        title='AI for Startups - OnScript AI'
        metaDesciption='Full-cycle AI development services for startups. From MVP to production, we help you build intelligent solutions that scale with your business.'
      />
      <Header />

      <ServiceHero 
        badge="AI for Startups"
        title="Turn Your Vision Into"
        titleHighlight="AI-Powered Reality"
        description="Starting with a prototype and scaling to full deployment, we help startups unlock rapid growth through smart and scalable AI solutions."
        ctaLink="/project-initial"
      />

      {/* Services Section */}
      <div className='py-20 md:py-28'>
        <div className='container-custom'>
          <CardColumn
            title="AI Services We Offer"
            subtitle="Tailored Solutions for Startups"
            description="Our AI engineers apply innovative techniques to every project, delivering solutions that drive growth and competitive advantage."
            cols={2}
            cards={startupServices}
          />
        </div>
      </div>

      {/* Solutions Section */}
      <div className='py-20 md:py-28'>
        <div className='container-custom'>
          <HorizontalScroll
            title="AI Solutions We Build"
            description="We design AI-driven systems that do more than just automate: they learn, adapt, and drive real results."
            items={aiSolutions}
            cardWidth={280}
          />
        </div>
      </div>

      {/* Why Section */}
      <div className='py-20 md:py-28'>
        <div className='container-custom'>
          <CardColumn
            title="Why OnScript AI?"
            description="Startups choose us because we understand the urgency, constraints, and ambition that define early-stage ventures."
            cols={2}
            cards={whyItems}
          />
        </div>
      </div>

      <ServiceCTA 
        title="Ready to Build Your AI Solution?"
        description="Let's turn your vision into intelligent features that scale with your users and business goals."
        buttonText="Get Started Today"
      />

      <Footer />
    </Layout>
  )
}

export default AIForStartupsPage
